import { useMemo, useState } from "react";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { isoToEU } from "../../lib/format";

/**
 * RatePreviewPopover — hover-triggered tooltip that renders the full
 * seasonal-tier ladder for a vehicle. Fetches `/settings/seasons` once so
 * we can label each season row by its date window.
 *
 * When a vehicle has no seasonal_rates at all, the popover falls back to
 * a single "Default daily rate" row so admins still get an at-a-glance
 * price summary without leaving the fleet table.
 */
export default function RatePreviewPopover({ vehicle, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ side: "right", top: 0, left: 0 });
  const { data: seasons } = useSafeFetch("/settings/seasons", {
    defaultValue: [], asList: true,
  });

  const seasonById = useMemo(() => {
    const m = {};
    for (const s of seasons || []) m[s.id] = s;
    return m;
  }, [seasons]);

  const rateRows = vehicle.seasonal_rates || [];
  const hasRates = rateRows.length > 0;

  // Position the popover relative to the triggering cell; flip to the
  // left when we're too close to the viewport's right edge.
  const handleEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.right;
    const side = spaceRight < 360 ? "left" : "right";
    setPos({
      side,
      top:  rect.top + window.scrollY - 4,
      left: side === "right" ? rect.right + 10 : rect.left - 320,
    });
    setOpen(true);
  };

  return (
    <span
      className="inline-flex items-center relative"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
      onFocus={handleEnter}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      data-testid={`rate-popover-trigger-${vehicle.id}`}
    >
      {children}
      {open && (
        <div
          className="fixed z-50 w-[340px] bg-white dark:bg-[#1F2937] border border-black/15 dark:border-white/15 shadow-xl rounded-lg p-4 text-xs pointer-events-none"
          style={{ top: pos.top, left: pos.left }}
          data-testid={`rate-popover-${vehicle.id}`}
        >
          <p className="uppercase tracking-[0.2em] text-[9px] text-neutral-500 dark:text-neutral-400 mb-2">
            {vehicle.name} · rate ladder
          </p>

          <div className="grid grid-cols-[1fr_auto] gap-1.5 py-1.5 border-b border-black/10 dark:border-white/10">
            <span className="text-neutral-700 dark:text-neutral-300">
              Group default (any date, no matching tier)
            </span>
            <span className="tabular-nums font-medium text-right">
              €{Number(vehicle.daily_rate || 0).toFixed(0)}/day
            </span>
          </div>

          {!hasRates && (
            <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-2 italic">
              No seasonal or tier overrides — every booking prices at the group default.
            </p>
          )}

          {hasRates && rateRows.map((row) => {
            const season = seasonById[row.season_id];
            const seasonLabel = season
              ? `${season.name} · ${isoToEU(season.start_date)} → ${isoToEU(season.end_date)}`
              : "Unknown season";
            const tiers = row.duration_tiers || [];
            return (
              <div key={row.season_id} className="mt-3">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  {seasonLabel}
                </p>
                {tiers.length === 0 ? (
                  <div className="flex justify-between mt-1">
                    <span className="text-neutral-700 dark:text-neutral-300">
                      Season base rate (all durations)
                    </span>
                    <span className="tabular-nums font-medium">
                      €{Number(row.daily_rate || 0).toFixed(0)}/day
                    </span>
                  </div>
                ) : (
                  <div className="mt-1 space-y-0.5">
                    {tiers.map((t, i) => (
                      <div
                        key={i}
                        className="flex justify-between"
                        data-testid={`rate-popover-tier-${vehicle.id}-${i}`}
                      >
                        <span className="text-neutral-700 dark:text-neutral-300">
                          {t.min_days === t.max_days
                            ? `${t.min_days} day`
                            : `${t.min_days}–${t.max_days} days`}
                        </span>
                        <span className="tabular-nums font-medium">
                          €{Number(t.daily_rate || 0).toFixed(0)}/day
                        </span>
                      </div>
                    ))}
                    {(row.daily_rate || 0) > 0 && (
                      <p className="text-[9px] text-neutral-500 dark:text-neutral-400 italic mt-0.5">
                        Season base €{Number(row.daily_rate).toFixed(0)} ignored while any tier matches.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <p className="text-[9px] text-neutral-500 dark:text-neutral-400 mt-3 pt-2 border-t border-black/10 dark:border-white/10">
            Booking flow picks the tier whose day-count window contains the rental length,
            then falls back to season base → group default.
          </p>
        </div>
      )}
    </span>
  );
}
